/**
 * 홈페이지 컴포넌트
 *
 * 앱의 메인 페이지입니다.
 * 탭으로 허밍 검색 / 가사 검색을 전환할 수 있습니다.
 */

import { useState, useRef } from 'react'
import { Search, Music, RotateCcw, AlertCircle, Play, Pause } from 'lucide-react'
import { useI18n, interpolate } from '../i18n'
import { useRateLimit, HUMMING_RATE_LIMIT, LYRICS_RATE_LIMIT } from '../hooks/useRateLimit'
import { AudioRecorder } from '../components/AudioRecorder'
import { SearchTabs, type SearchTab } from '../components/SearchTabs'
import { SongCard } from '../components/SongCard'
import { LoadingSpinner } from '../components/LoadingSpinner'
import { ResultsHeader } from '../components/ResultsHeader'
import { api } from '../services/api'
import type { HummingResult, LyricsResult } from '../types'

// =============================================================================
// 타입 정의
// =============================================================================

/** 페이지 상태 (검색 진행 상황) */
type PageState = 'idle' | 'recorded' | 'uploading' | 'searching' | 'results' | 'error'

// =============================================================================
// 메인 컴포넌트
// =============================================================================

export function HomePage() {
  const { t, language } = useI18n()

  // Rate limiting
  const hummingRateLimit = useRateLimit(HUMMING_RATE_LIMIT)
  const lyricsRateLimit = useRateLimit(LYRICS_RATE_LIMIT)

  // ---------------------------------------------------------------------------
  // 상태 관리
  // ---------------------------------------------------------------------------

  const [activeTab, setActiveTab] = useState<SearchTab>('lyrics')
  const [pageState, setPageState] = useState<PageState>('idle')

  // 녹음 관련
  const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [playbackTime, setPlaybackTime] = useState(0)
  const [audioDuration, setAudioDuration] = useState(0)
  const audioRef = useRef<HTMLAudioElement | null>(null)

  // 검색 결과
  const [hummingResults, setHummingResults] = useState<HummingResult[]>([])
  const [lyricsResults, setLyricsResults] = useState<LyricsResult[]>([])

  // 미리듣기 상태
  const [previewPlayingId, setPreviewPlayingId] = useState<string | null>(null)
  const [previewLoading, setPreviewLoading] = useState<string | null>(null)
  const previewAudioRef = useRef<HTMLAudioElement | null>(null)

  // 기타
  const [error, setError] = useState<string | null>(null)
  const [processingTime, setProcessingTime] = useState<number | null>(null)
  const [lyricsQuery, setLyricsQuery] = useState('')

  // ---------------------------------------------------------------------------
  // 핸들러
  // ---------------------------------------------------------------------------

  const handleRecordingComplete = (blob: Blob, durationSec: number) => {
    setRecordedBlob(blob)
    setPageState('recorded')
    setError(null)
    setAudioDuration(durationSec)

    const url = URL.createObjectURL(blob)
    const audio = new Audio(url)
    audioRef.current = audio

    audio.addEventListener('timeupdate', () => setPlaybackTime(audio.currentTime))
    audio.addEventListener('ended', () => {
      setIsPlaying(false)
      setPlaybackTime(0)
    })
  }

  const togglePlayback = () => {
    if (!audioRef.current) return
    if (isPlaying) {
      audioRef.current.pause()
    } else {
      audioRef.current.play()
    }
    setIsPlaying(!isPlaying)
  }

  const togglePreviewPlayback = async (song: HummingResult | LyricsResult) => {
    if (previewPlayingId === song.platform_id) {
      previewAudioRef.current?.pause()
      setPreviewPlayingId(null)
      return
    }

    if (previewAudioRef.current) {
      previewAudioRef.current.pause()
    }

    if (song.preview_url) {
      const audio = new Audio(song.preview_url)
      audio.play()
      audio.onended = () => setPreviewPlayingId(null)
      previewAudioRef.current = audio
      setPreviewPlayingId(song.platform_id)
      return
    }

    setPreviewLoading(song.platform_id)
    try {
      const { preview_url } = await api.getPreviewUrl(song.platform_id)
      if (preview_url) {
        const audio = new Audio(preview_url)
        audio.play()
        audio.onended = () => setPreviewPlayingId(null)
        previewAudioRef.current = audio
        setPreviewPlayingId(song.platform_id)
      }
    } catch (err) {
      console.error('Failed to get preview URL:', err)
    } finally {
      setPreviewLoading(null)
    }
  }

  const handleHummingSearch = async () => {
    if (!recordedBlob) return

    if (!hummingRateLimit.recordRequest()) {
      setError(interpolate(t('rateLimitWarning'), { seconds: hummingRateLimit.secondsUntilReset }))
      setPageState('error')
      return
    }

    setPageState('uploading')
    setError(null)

    if (audioRef.current) {
      audioRef.current.pause()
      setIsPlaying(false)
    }
    if (previewAudioRef.current) {
      previewAudioRef.current.pause()
      setPreviewPlayingId(null)
    }

    try {
      setPageState('searching')
      const response = await api.searchByHumming(recordedBlob)
      setHummingResults(response.results)
      setProcessingTime(response.processing_time_ms)
      setPageState('results')
    } catch (err: unknown) {
      console.error('Humming search error:', err)
      let errorMessage = t('error')
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosErr = err as { response?: { data?: { detail?: string } } }
        if (axiosErr.response?.data?.detail) {
          errorMessage = axiosErr.response.data.detail
        }
      } else if (err instanceof Error) {
        errorMessage = err.message
      }
      setError(errorMessage)
      setPageState('error')
    }
  }

  const handleLyricsSearch = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!lyricsQuery.trim()) return

    if (!lyricsRateLimit.recordRequest()) {
      setError(interpolate(t('rateLimitWarning'), { seconds: lyricsRateLimit.secondsUntilReset }))
      setPageState('error')
      return
    }

    setPageState('searching')
    setError(null)

    if (previewAudioRef.current) {
      previewAudioRef.current.pause()
      setPreviewPlayingId(null)
    }

    try {
      const response = await api.searchByLyrics(lyricsQuery)
      setLyricsResults(response.results)
      setPageState('results')
    } catch (err) {
      console.error('Lyrics search error:', err)
      setError(err instanceof Error ? err.message : t('error'))
      setPageState('error')
    }
  }

  const handleRetry = () => {
    if (audioRef.current) {
      audioRef.current.pause()
      URL.revokeObjectURL(audioRef.current.src)
      audioRef.current = null
    }
    if (previewAudioRef.current) {
      previewAudioRef.current.pause()
      previewAudioRef.current = null
    }

    setPageState('idle')
    setRecordedBlob(null)
    setIsPlaying(false)
    setPlaybackTime(0)
    setAudioDuration(0)
    setHummingResults([])
    setLyricsResults([])
    setError(null)
    setProcessingTime(null)
    setPreviewPlayingId(null)
  }

  const handleTabChange = (tab: SearchTab) => {
    setActiveTab(tab)
    handleRetry()
  }

  const formatSeconds = (sec: number): string => {
    const minutes = Math.floor(sec / 60)
    const seconds = Math.floor(sec % 60)
    return `${minutes}:${seconds.toString().padStart(2, '0')}`
  }

  // ---------------------------------------------------------------------------
  // 렌더링
  // ---------------------------------------------------------------------------

  const currentResults = activeTab === 'humming' ? hummingResults : lyricsResults

  return (
    <div className="min-h-screen bg-dark-900 relative">
      {/* 배경 효과 */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-20 left-1/2 -translate-x-1/2 w-[1000px] h-[600px] bg-accent-500/[0.07] rounded-full blur-3xl" />
        <div className="absolute top-40 left-1/2 -translate-x-1/2 w-[250px] h-[250px] border border-accent-500/15 rounded-full" />
        <div className="absolute top-40 left-1/2 w-[400px] h-[400px] border border-accent-500/10 rounded-full" style={{ transform: 'translateX(-50%) translateY(-75px)' }} />
        <div className="absolute top-40 left-1/2 w-[600px] h-[600px] border border-accent-500/[0.07] rounded-full" style={{ transform: 'translateX(-50%) translateY(-175px)' }} />
        <div className="absolute top-40 left-1/2 w-[850px] h-[850px] border border-accent-500/[0.04] rounded-full" style={{ transform: 'translateX(-50%) translateY(-300px)' }} />
        <div className="absolute top-40 left-1/2 w-[1100px] h-[1100px] border border-accent-500/[0.02] rounded-full" style={{ transform: 'translateX(-50%) translateY(-425px)' }} />
        <div className="absolute top-0 -left-32 w-64 h-[800px] bg-accent-500/[0.03] blur-3xl" />
        <div className="absolute top-0 -right-32 w-64 h-[800px] bg-accent-500/[0.03] blur-3xl" />
      </div>

      {/* 히어로 섹션 */}
      <section className="relative text-text-primary py-12">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <h1 className="text-4xl font-bold mb-3">{t('heroTitle')}</h1>
          <p className="text-text-secondary text-lg">{t('heroSubtitle')}</p>
        </div>
      </section>

      {/* 메인 컨텐츠 */}
      <main className="relative max-w-2xl mx-auto px-4">
        <div className="bg-dark-700/90 backdrop-blur-sm rounded-2xl shadow-lg overflow-hidden border border-dark-500/50">

          {/* 탭 */}
          <SearchTabs activeTab={activeTab} onTabChange={handleTabChange} />

          {/* 탭 컨텐츠 */}
          <div className="p-6">
            {/* 허밍 탭 */}
            {activeTab === 'humming' && (
              <>
                {pageState === 'idle' && (
                  <>
                    <div className="mb-6 p-4 bg-accent-500/10 border border-accent-500/30 rounded-xl text-center">
                      <p className="text-accent-400 font-medium">{t('hummingGuide')}</p>
                    </div>
                    <AudioRecorder
                      onRecordingComplete={handleRecordingComplete}
                      maxDuration={15}
                      minDuration={5}
                    />
                  </>
                )}

                {pageState === 'recorded' && recordedBlob && (
                  <div className="flex flex-col items-center gap-6 py-8">
                    <div className="flex items-center gap-4 w-full max-w-md bg-dark-600 rounded-full px-4 py-3">
                      <button
                        onClick={togglePlayback}
                        className="w-12 h-12 bg-accent-500 rounded-full flex items-center justify-center hover:bg-accent-600 transition-colors"
                      >
                        {isPlaying ? (
                          <Pause className="w-6 h-6 text-white" fill="white" />
                        ) : (
                          <Play className="w-6 h-6 text-white ml-1" fill="white" />
                        )}
                      </button>
                      <div className="flex-1">
                        <div className="h-2 bg-dark-500 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-accent-500 transition-all duration-100"
                            style={{ width: `${audioDuration > 0 ? (playbackTime / audioDuration) * 100 : 0}%` }}
                          />
                        </div>
                        <div className="flex justify-between text-xs text-text-muted mt-1">
                          <span>{formatSeconds(playbackTime)}</span>
                          <span>{formatSeconds(audioDuration)}</span>
                        </div>
                      </div>
                    </div>

                    <div className="flex gap-3">
                      <button
                        onClick={handleRetry}
                        className="flex items-center gap-2 px-5 py-2.5 bg-dark-600 text-text-secondary rounded-lg hover:bg-dark-500 transition-colors"
                      >
                        <RotateCcw className="w-4 h-4" />
                        {language === 'ko' ? '다시 녹음' : 'Re-record'}
                      </button>
                      <button
                        onClick={handleHummingSearch}
                        className="flex items-center gap-2 px-6 py-2.5 bg-accent-500 text-white rounded-lg hover:bg-accent-600 transition-colors font-medium"
                      >
                        <Search className="w-4 h-4" />
                        {t('search')}
                      </button>
                    </div>
                  </div>
                )}

                {(pageState === 'uploading' || pageState === 'searching') && (
                  <LoadingSpinner
                    title={pageState === 'uploading' ? t('uploading') : t('searching')}
                    description={pageState === 'uploading' ? t('uploadingDesc') : t('searchingDesc')}
                  />
                )}
              </>
            )}

            {/* 가사 탭 */}
            {activeTab === 'lyrics' && (
              <>
                {(pageState === 'idle' || pageState === 'results') && (
                  <div className="space-y-4">
                    <form onSubmit={handleLyricsSearch}>
                      <div className="relative">
                        <input
                          type="text"
                          value={lyricsQuery}
                          onChange={(e) => setLyricsQuery(e.target.value)}
                          placeholder={t('lyricsPlaceholder')}
                          className="w-full px-5 py-4 pr-14 rounded-xl border border-dark-500
                                   bg-dark-600 text-text-primary placeholder-text-muted
                                   focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-transparent
                                   text-lg"
                        />
                        <button
                          type="submit"
                          disabled={!lyricsQuery.trim()}
                          className="absolute right-3 top-1/2 -translate-y-1/2
                                   w-10 h-10 bg-accent-500 rounded-full
                                   flex items-center justify-center
                                   hover:bg-accent-600 transition-colors
                                   disabled:bg-dark-500 disabled:cursor-not-allowed"
                        >
                          <Search className="w-5 h-5 text-white" />
                        </button>
                      </div>
                    </form>
                    <p className="text-sm text-text-muted text-center">{t('lyricsTip')}</p>
                  </div>
                )}

                {pageState === 'searching' && (
                  <LoadingSpinner title={t('searching')} description={t('searchingDesc')} />
                )}
              </>
            )}

            {/* 에러 상태 */}
            {pageState === 'error' && (
              <div className="text-center py-12">
                <div className="w-16 h-16 mx-auto mb-6 bg-red-500/20 rounded-full flex items-center justify-center">
                  <AlertCircle className="w-8 h-8 text-red-500" />
                </div>
                <h2 className="text-xl font-semibold text-text-primary mb-2">{t('error')}</h2>
                <p className="text-text-secondary mb-6">{error}</p>
                <button
                  onClick={handleRetry}
                  className="px-6 py-3 bg-accent-500 text-white rounded-lg hover:bg-accent-600 transition-colors"
                >
                  {t('retry')}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* 검색 결과 */}
        {pageState === 'results' && (
          <div className="mt-6 space-y-4">
            <ResultsHeader
              resultCount={currentResults.length}
              processingTime={activeTab === 'humming' ? processingTime : null}
              onRetry={handleRetry}
            />

            {currentResults.length > 0 ? (
              <div className="space-y-3">
                {currentResults.map((song, index) => (
                  <SongCard
                    key={song.platform_id}
                    song={song}
                    rank={index + 1}
                    previewPlayingId={previewPlayingId}
                    previewLoading={previewLoading}
                    onTogglePreview={togglePreviewPlayback}
                  />
                ))}
              </div>
            ) : (
              <div className="bg-dark-700 rounded-xl shadow-md p-12 text-center border border-dark-500/50">
                <Music className="w-16 h-16 mx-auto text-text-muted mb-4" />
                <h3 className="text-xl font-semibold text-text-primary mb-2">{t('noResults')}</h3>
                <p className="text-text-secondary mb-6">{t('noResultsHint')}</p>
                <button
                  onClick={handleRetry}
                  className="px-6 py-3 bg-accent-500 text-white rounded-lg hover:bg-accent-600 transition-colors"
                >
                  {t('retry')}
                </button>
              </div>
            )}
          </div>
        )}

        {/* 팁 섹션 */}
        {activeTab === 'humming' && pageState === 'idle' && (
          <div className="mt-6 bg-dark-700 rounded-xl shadow-md p-6 border border-dark-500/50">
            <h3 className="font-semibold text-text-primary mb-3">{t('tipsTitle')}</h3>
            <ul className="space-y-2 text-sm text-text-secondary">
              <li>• {t('tip1')}</li>
              <li>• {t('tip2')}</li>
              <li>• {t('tip3')}</li>
              <li>• {t('tip4')}</li>
            </ul>
          </div>
        )}

        <div className="h-8" />
      </main>
    </div>
  )
}
